import { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import CloseOutlinedIcon from '@ant-design/icons/CloseOutlined'
import DownloadOutlinedIcon from '@ant-design/icons/DownloadOutlined'
import RotateLeftOutlinedIcon from '@ant-design/icons/RotateLeftOutlined'
import RotateRightOutlinedIcon from '@ant-design/icons/RotateRightOutlined'
import ZoomInOutlinedIcon from '@ant-design/icons/ZoomInOutlined'
import ZoomOutOutlinedIcon from '@ant-design/icons/ZoomOutOutlined'

import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import Grow from '@mui/material/Grow'
import { alpha } from '@mui/material/styles'
import Zoom from '@mui/material/Zoom'
import IconButton from '@components/_mui/IconButton'

import Image from '@shared/components/src/Image'

const MIN_ZOOM = 0.5
const MAX_ZOOM = 2
const ZOOM_STEP = 0.25
const ROTATION_STEP = 90
const DRAG_BOUNDARY_MARGIN = 0.2 // Allow dragging until 20% of the image is outside the container

/**
 * A full-screen image viewer with zoom, rotation, and download capabilities.
 * Uses a render prop pattern to render the trigger element.
 */
export default function ImageFullScreenViewer({ image, base64 = false, children }) {
  const [isOpen, setIsOpen] = useState(false)
  const [zoomLevel, setZoomLevel] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 })

  const imageContainerRef = useRef(null)
  const imageRef = useRef(null)

  const imageSrc = base64 ? `data:image/*;base64,${image}` : image

  useEffect(() => {
    if (zoomLevel === 1) {
      setPosition({ x: 0, y: 0 })
    }
  }, [zoomLevel])

  const updateImageDimensions = useCallback(() => {
    if (imageRef.current) {
      const { offsetWidth, offsetHeight } = imageRef.current
      setImageDimensions({
        width: offsetWidth,
        height: offsetHeight,
      })
    }
  }, [])

  useEffect(() => {
    const imgElement = imageRef.current
    if (!imgElement) return

    if (imgElement.complete) {
      updateImageDimensions()
    } else {
      imgElement.addEventListener('load', updateImageDimensions)
    }

    return () => {
      imgElement.removeEventListener('load', updateImageDimensions)
    }
  }, [image, isOpen, updateImageDimensions])

  const handleOpenViewer = useCallback(() => setIsOpen(true), [])

  const handleClose = useCallback(() => {
    setIsOpen(false)

    setTimeout(() => {
      setZoomLevel(1)
      setRotation(0)
      setPosition({ x: 0, y: 0 })
    }, 300)
  }, [])

  const handleZoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM))
  }, [])

  const handleZoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.max(prev - ZOOM_STEP, MIN_ZOOM))
  }, [])

  const handleRotateLeft = useCallback(() => {
    setRotation((prev) => prev - ROTATION_STEP)
    setPosition({ x: 0, y: 0 })
  }, [])

  const handleRotateRight = useCallback(() => {
    setRotation((prev) => prev + ROTATION_STEP)
    setPosition({ x: 0, y: 0 })
  }, [])

  const handleDownload = useCallback(async () => {
    if (!image) return

    const link = document.createElement('a')

    if (base64) {
      link.href = `data:image/png;base64,${image}`
      link.download = `image-${Date.now()}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      const blob = await fetch(image).then((r) => r.blob())
      const url = URL.createObjectURL(blob)
      link.href = url
      link.download = `image-${Date.now()}.jpg`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }
  }, [image, base64])

  // Calculate boundary constraints for dragging
  const calculateBoundaries = useCallback(
    (deltaX, deltaY) => {
      if (!imageContainerRef.current || imageDimensions.width === 0) {
        return { x: deltaX, y: deltaY }
      }

      const containerRect = imageContainerRef.current.getBoundingClientRect()
      const containerWidth = containerRect.width
      const containerHeight = containerRect.height

      const zoomedWidth = imageDimensions.width * zoomLevel
      const zoomedHeight = imageDimensions.height * zoomLevel

      const maxOffsetX = Math.max(0, (zoomedWidth - containerWidth) / 2 + containerWidth * DRAG_BOUNDARY_MARGIN)
      const maxOffsetY = Math.max(0, (zoomedHeight - containerHeight) / 2 + containerHeight * DRAG_BOUNDARY_MARGIN)

      let newX = position.x + deltaX
      let newY = position.y + deltaY

      newX = Math.max(-maxOffsetX, Math.min(newX, maxOffsetX))
      newY = Math.max(-maxOffsetY, Math.min(newY, maxOffsetY))

      return {
        x: newX - position.x,
        y: newY - position.y,
      }
    },
    [imageDimensions, zoomLevel, position]
  )

  const handleMouseDown = useCallback(
    (e) => {
      if (zoomLevel > 1) {
        setIsDragging(true)
        setDragStart({ x: e.clientX, y: e.clientY })
        e.preventDefault()
      }
    },
    [zoomLevel]
  )

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging || zoomLevel <= 1) return

      const rawDeltaX = e.clientX - dragStart.x
      const rawDeltaY = e.clientY - dragStart.y

      const { x: boundedDeltaX, y: boundedDeltaY } = calculateBoundaries(rawDeltaX, rawDeltaY)

      setPosition((prev) => ({
        x: prev.x + boundedDeltaX,
        y: prev.y + boundedDeltaY,
      }))

      setDragStart({ x: e.clientX, y: e.clientY })
    },
    [isDragging, zoomLevel, dragStart, calculateBoundaries]
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Touch handlers for mobile
  const handleTouchStart = useCallback(
    (e) => {
      if (zoomLevel > 1 && e.touches.length === 1) {
        setIsDragging(true)
        setDragStart({
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
        })
      }
    },
    [zoomLevel]
  )

  const handleTouchMove = useCallback(
    (e) => {
      if (!isDragging || zoomLevel <= 1 || e.touches.length !== 1) return

      const rawDeltaX = e.touches[0].clientX - dragStart.x
      const rawDeltaY = e.touches[0].clientY - dragStart.y

      const { x: boundedDeltaX, y: boundedDeltaY } = calculateBoundaries(rawDeltaX, rawDeltaY)

      setPosition((prev) => ({
        x: prev.x + boundedDeltaX,
        y: prev.y + boundedDeltaY,
      }))

      setDragStart({
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      })

      e.preventDefault()
    },
    [isDragging, zoomLevel, dragStart, calculateBoundaries]
  )

  const handleTouchEnd = useCallback(() => setIsDragging(false), [])

  return (
    <>
      {children(handleOpenViewer)}

      <Dialog fullScreen open={isOpen} onClose={handleClose} slots={{ transition: Grow }}>
        <ViewerContainer>
          <ImageContainer
            ref={imageContainerRef}
            isDragging={isDragging}
            zoomLevel={zoomLevel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {image && (
              <TransformedImage position={position} zoomLevel={zoomLevel} rotation={rotation} isDragging={isDragging}>
                <Image
                  ref={imageRef}
                  fit="contain"
                  src={imageSrc}
                  duration={300}
                  sx={{ maxHeight: '90vh', maxWidth: '90vw', pointerEvents: 'none' }}
                  draggable={false}
                  onLoad={updateImageDimensions}
                />
              </TransformedImage>
            )}
          </ImageContainer>

          <ControlPanel>
            <ControlButton
              delay="200ms"
              isOpen={isOpen}
              onClick={handleZoomOut}
              disabled={zoomLevel <= MIN_ZOOM}
              icon={<ZoomOutOutlinedIcon />}
            />
            <ControlButton
              delay="300ms"
              isOpen={isOpen}
              onClick={handleZoomIn}
              disabled={zoomLevel >= MAX_ZOOM}
              icon={<ZoomInOutlinedIcon />}
            />
            <ControlButton delay="400ms" isOpen={isOpen} onClick={handleRotateLeft} icon={<RotateLeftOutlinedIcon />} />
            <ControlButton delay="500ms" isOpen={isOpen} onClick={handleRotateRight} icon={<RotateRightOutlinedIcon />} />
            <ControlButton delay="600ms" isOpen={isOpen} onClick={handleDownload} color="primary" icon={<DownloadOutlinedIcon />} />
            <ControlButton delay="700ms" isOpen={isOpen} onClick={handleClose} color="error" icon={<CloseOutlinedIcon />} />
          </ControlPanel>
        </ViewerContainer>
      </Dialog>
    </>
  )
}

ImageFullScreenViewer.propTypes = {
  /** Base64 or URL image source */
  image: PropTypes.string,

  /** Whether the image is in base64 format */
  base64: PropTypes.bool,

  /**
   * Render prop that receives an onClick handler
   * Example: {(openViewer) => <img src="..." onClick={openViewer} />}
   */
  children: PropTypes.func.isRequired,
}

const ViewerContainer = ({ children }) => (
  <Box
    sx={{
      height: '100%',
      width: '100%',
      backgroundColor: 'background.paper',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      userSelect: 'none',
    }}
  >
    {children}
  </Box>
)

const ImageContainer = ({ children, isDragging, zoomLevel, ...props }) => (
  <Box
    {...props}
    sx={{
      height: '100%',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: isDragging ? 'grabbing' : zoomLevel > 1 ? 'grab' : 'default',
    }}
  >
    {children}
  </Box>
)

const TransformedImage = ({ children, position, zoomLevel, rotation, isDragging }) => (
  <Box
    sx={{
      transform: `translate(${position.x}px, ${position.y}px) scale(${zoomLevel}) rotate(${rotation}deg)`,
      transition: isDragging ? 'none' : 'transform 0.3s ease',
      transformOrigin: 'center',
    }}
  >
    {children}
  </Box>
)

const ControlPanel = ({ children }) => (
  <Box
    sx={{
      position: 'fixed',
      bottom: 16,
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      gap: 1,
      zIndex: 9999,
      padding: 1,
      borderRadius: 2,
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.7),
      boxShadow: (theme) => theme.shadows[3],
    }}
  >
    {children}
  </Box>
)

const ControlButton = ({ delay, isOpen, onClick, disabled, color, icon }) => (
  <Zoom in={isOpen} style={{ transitionDelay: isOpen ? delay : '0ms' }}>
    <IconButton variant="contained" onClick={onClick} disabled={disabled} color={color}>
      {icon}
    </IconButton>
  </Zoom>
)
